import { useTRPC } from "@/lib/trpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { DashboardPageHeader, DashboardPageShell } from "@/components/patterns/headers";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/avatar";
import { X, UserPlus, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/_dashboard/$brand/reports/$report")({
  component: RouteComponent,
});

function ReportDetailSkeleton() {
  return (
    <DashboardPageShell contentClassName="space-y-6">
      <div className="space-y-3">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-8 w-96" />
        <Skeleton className="h-4 w-64" />
      </div>
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full rounded-lg" />
        ))}
      </div>
    </DashboardPageShell>
  );
}

function RouteComponent() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { report } = Route.useParams();
  const { data: reportData } = useQuery(
    trpc.reports.getReport.queryOptions(
      { id: report },
      { enabled: !!report },
    ),
  );

  const { mutate: updateReportTaskStep } = useMutation({
    ...trpc.reports.updateReportTaskStep.mutationOptions({}),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.reports.getReport.queryOptions({ id: report }));
    },
  });

  const { mutate: assignReportTask } = useMutation({
    ...trpc.reports.assignReportTask.mutationOptions({}),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.reports.getReport.queryOptions({ id: report }));
    },
  });

  const { data: userProfiles } = useQuery(
    trpc.userProfiles.getOrganizationUserProfiles.queryOptions({}),
  );

  if (!reportData || !userProfiles) return <ReportDetailSkeleton />;

  const totalTasks = reportData.reportTasks?.length ?? 0;
  const completedTasks = reportData.reportTasks?.filter((t) =>
    t.reportTaskSteps.every((s) => s.completedAt !== null),
  ).length ?? 0;
  const overallProgress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <DashboardPageShell contentClassName="space-y-6">
      <DashboardPageHeader
        size="detail"
        title={reportData.title}
        description={reportData.description}
        meta={new Date(reportData.createdAt).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })}
        below={
          totalTasks > 0 ? (
            <div className="flex items-center gap-3 pt-1">
              <Progress value={overallProgress} className="h-1.5 max-w-xs bg-brand-ink/[0.06]" />
              <span className="text-xs font-medium text-muted-foreground">
                {completedTasks} of {totalTasks} tasks complete
              </span>
            </div>
          ) : undefined
        }
      />

      {/* Tasks */}
      <div className="space-y-3">
        {reportData.reportTasks?.map((task) => {
          const stepsCompleted = task.reportTaskSteps.filter((s) => s.completedAt !== null).length;
          const stepsTotal = task.reportTaskSteps.length;
          const isComplete = stepsTotal > 0 && stepsCompleted === stepsTotal;
          const taskProgress = stepsTotal > 0 ? Math.round((stepsCompleted / stepsTotal) * 100) : 0;

          return (
            <div
              key={task.id}
              className={`rounded-lg border transition-colors ${
                isComplete
                  ? "border-success/20 bg-success-light/50"
                  : "border-border/60 bg-card"
              }`}
            >
              {/* Task header */}
              <div className="flex items-start gap-3 px-5 pt-4 pb-3">
                <Checkbox
                  className="mt-0.5 cursor-pointer"
                  checked={
                    isComplete
                      ? true
                      : stepsCompleted > 0
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={() => {
                    const targetStatus = !isComplete;
                    task.reportTaskSteps.forEach((step) => {
                      updateReportTaskStep({
                        id: step.id,
                        completed: targetStatus,
                      });
                    });
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2.5">
                    <h2
                      className={`text-[0.9375rem] font-semibold leading-snug ${
                        isComplete ? "text-success" : "text-brand-ink"
                      }`}
                    >
                      {task.title}
                    </h2>
                    <Badge
                      variant="outline"
                      className="shrink-0 text-[10px] font-medium border-border text-muted-foreground"
                    >
                      {task.type}
                    </Badge>
                    {isComplete && (
                      <CheckCircle2 className="size-3.5 shrink-0 text-success" />
                    )}
                  </div>
                  {/* Progress bar for this task */}
                  {stepsTotal > 0 && !isComplete && (
                    <div className="mt-2 flex items-center gap-2.5">
                      <Progress value={taskProgress} className="h-1 max-w-[120px] bg-brand-ink/[0.06]" />
                      <span className="text-[11px] text-muted-foreground">
                        {stepsCompleted}/{stepsTotal}
                      </span>
                    </div>
                  )}
                </div>

                {/* Assignee */}
                <div className="shrink-0">
                  <DropdownMenu>
                    <DropdownMenuTrigger className="focus:outline-none focus-visible:ring-0 cursor-pointer">
                      {task.assignedTo ? (
                        <div className="flex items-center gap-2 rounded-md border border-border/60 px-2.5 py-1.5 transition-colors hover:bg-muted/50">
                          <UserAvatar
                            userId={task.assignedTo.id}
                            name={task.assignedTo.fullName}
                            email={task.assignedTo.email}
                            avatarUrl={task.assignedTo.avatarUrl}
                            size="xs"
                          />
                          <span className="text-xs font-medium text-brand-ink">
                            {task.assignedTo.fullName}
                          </span>
                        </div>
                      ) : (
                        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground">
                          <UserPlus className="size-3" />
                          Assign
                        </Button>
                      )}
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="bottom" align="end">
                      {userProfiles?.items.map((user) => (
                        <DropdownMenuItem
                          key={user.id}
                          onClick={() =>
                            assignReportTask({ reportTaskId: task.id, assignedToId: user.id })
                          }
                        >
                          <UserAvatar
                            userId={user.id}
                            name={user.fullName}
                            email={user.email}
                            avatarUrl={user.avatarUrl}
                            size="xs"
                          />
                          <span className="text-sm font-medium">{user.fullName}</span>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() =>
                          assignReportTask({ reportTaskId: task.id, assignedToId: null })
                        }
                      >
                        <X className="size-4" />
                        Unassign
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Steps */}
              {stepsTotal > 0 && (
                <div className="border-t border-border/40 px-5 py-3">
                  <div className="space-y-0.5">
                    {task.reportTaskSteps
                      ?.sort((a, b) => a.order - b.order)
                      .map((step) => {
                        const isDone = step.completedAt !== null;
                        return (
                          <button
                            type="button"
                            key={step.id}
                            className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-brand-ink/[0.03] cursor-pointer"
                            onClick={() =>
                              updateReportTaskStep({ id: step.id, completed: !step.completedAt })
                            }
                          >
                            <Checkbox
                              checked={isDone}
                              className="pointer-events-none shrink-0"
                            />
                            <span
                              className={`text-[0.8125rem] leading-snug ${
                                isDone
                                  ? "text-muted-foreground/60 line-through"
                                  : "text-brand-ink/80"
                              }`}
                            >
                              {step.description}
                            </span>
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </DashboardPageShell>
  );
}
